import React, { useState, useCallback, useRef, useEffect } from 'react';
import classNames from 'clsx';
import { ClickAwayListener, Popper, ButtonBase } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import replace from 'lodash-es/replace';

import { TagManagerService } from 'app/services/tagManager.service';
import { FormTooltipProps } from 'app/components/FormTooltip/FormTooltip.interfaces';
import { CloseIconButton } from 'app/components/CloseIconButton';
import { SafeHtml } from 'app/components/SafeHtml';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import { useStyles } from './FormTooltip.styles';

const HOVER_CLOSE_DELAY = 150;

export default function FormTooltip(props: FormTooltipProps) {
  const {
    className,
    content,
    icon,
    orangeIcon,
    inputId,
    disablePortal,
    openOnHover,
    placement = 'right',
    offsetDistance = 0,
  } = props;
  const classes = useStyles(props);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [arrowEl, setArrowEl] = useState<null | HTMLElement>(null);
  const [tooltipKey, setTooltipKey] = useState(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isHoverEnabled = Boolean(openOnHover) && !isMobileOrTablet();

  const getContainer = () =>
    (document.querySelector('.modal-opened') as HTMLElement | null) ||
    document.body;

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closeTooltip = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
  }, [clearCloseTimeout]);

  const openTooltip = (element: HTMLElement) => {
    clearCloseTimeout();
    setAnchorEl(element);
    setTooltipKey((prev) => prev + 1);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl) {
      openTooltip(event.currentTarget);
      if (inputId) {
        TagManagerService.addDataLayer({
          event: 'infotip_click',
          field_name: inputId,
        });
      }
    } else {
      closeTooltip();
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!isHoverEnabled) {
      return;
    }
    if (anchorEl) {
      clearCloseTimeout();
      return;
    }
    openTooltip(event.currentTarget);
  };

  const handleMouseLeave = () => {
    if (!isHoverEnabled) {
      return;
    }
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(closeTooltip, HOVER_CLOSE_DELAY);
  };

  const handlePopoverMouseEnter = () => {
    if (!isHoverEnabled) {
      return;
    }
    clearCloseTimeout();
  };

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const handleArrowRef = (element: HTMLElement) => {
    setArrowEl(element);
  };

  useEffect(() => {
    const container =
      (document.querySelector('.modal-opened') as HTMLElement | null) ||
      document.body;

    if (!container) {
      return;
    }

    const handleScrollLikeEvent = (event: Event) => {
      const popover = container.querySelector(
        `.${classes.popover}`
      ) as HTMLElement | null;

      if (
        popover &&
        event.target instanceof Node &&
        popover.contains(event.target as Node)
      ) {
        return;
      }

      closeTooltip();
    };

    scrollContainerRef.current = container;

    container.addEventListener('scroll', handleScrollLikeEvent, {
      passive: true,
    });
    container.addEventListener('wheel', handleScrollLikeEvent, {
      passive: true,
    });
    container.addEventListener('touchmove', handleScrollLikeEvent, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScrollLikeEvent);
      container.removeEventListener('wheel', handleScrollLikeEvent);
      container.removeEventListener('touchmove', handleScrollLikeEvent);
    };
  }, [classes.popover, closeTooltip]);

  if (!content) {
    return null;
  }

  const tooltipContent =
    typeof content === 'string' ? (
      <SafeHtml
        html={replace(
          content,
          '%PUBLIC_URL%',
          process.env.PUBLIC_URL as string
        )}
        component={'div'}
      />
    ) : (
      <div>{content}</div>
    );

  const open = Boolean(anchorEl);

  const Icon = icon || Help;

  return (
    <div className={classNames(classes.root, className)}>
      <ButtonBase
        className={classes.iconButton}
        aria-label={'podpowiedź'}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon className={orangeIcon ? classes.orangeIcon : classes.icon} />
      </ButtonBase>
      <Popper
        key={tooltipKey}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        container={getContainer}
        disablePortal={disablePortal}
        modifiers={{
          preventOverflow: true,
          offset: {
            offset: `0, ${offsetDistance}`,
          },
          arrow: {
            enabled: true,
            element: arrowEl,
          },
        }}
        className={classes.popover}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            if (
              anchorEl &&
              event.target instanceof Node &&
              anchorEl.contains(event.target)
            ) {
              return;
            }
            closeTooltip();
          }}
          mouseEvent={'onMouseUp'}
        >
          <div
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <span className={'arrow'} ref={handleArrowRef} />
            <div className={classes.popoverContent}>
              <CloseIconButton
                onClick={closeTooltip}
                className={classes.closeButton}
              />
              <div className={classes.tooltipContent}>{tooltipContent}</div>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
}
